import { motion } from "framer-motion";
import { Wind, Gauge, Plug, MapPin, BarChart3, Shield, Wrench, Thermometer } from "lucide-react";
import windFarmAerial from "../assets/wind-farm-aerial.jpg";
import turbineCloseup from "../assets/turbine-closeup.jpg";
import windImg from "../assets/wind-energy.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";
import StatsGrid from "../components/StatsGrid";
import EnergyFlowSVG from "../components/EnergyFlowSVG";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const WindEnergy = () => (
  <div>
    <PageHero
      eyebrow="Wind Energy Division"
      title={<>Harnessing the Raw Power of <span className="energy-gradient-text">Wind</span></>}
      description="Our wind division operates 14 onshore farms across the UK and Northern Europe, utilizing Vestas V150-6.0 MW turbines with a combined rated capacity exceeding 520 MW."
      backgroundImage={windFarmAerial}
    />

    <Section>
      <StatsGrid stats={[
        { value: "520 MW", label: "Total Wind Capacity" },
        { value: "14", label: "Active Wind Farms" },
        { value: "42%", label: "Avg Capacity Factor" },
        { value: "87", label: "Individual Turbines" },
      ]} />
    </Section>

    {/* Overview */}
    <Section className="bg-secondary/20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...fadeUp}>
          <span className="font-display text-xs tracking-[0.3em] uppercase text-primary mb-3 block">Technology</span>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Next-Generation Turbine Technology</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Each AeroVolt wind farm deploys Vestas V150-6.0 MW turbines — among the most advanced onshore wind turbines in commercial operation. With 150-meter rotor diameters, these turbines capture wind energy across a sweep area of 17,671 m², maximizing energy extraction even in moderate wind conditions.
          </p>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Our sites are selected using 3+ years of meteorological data analysis, LiDAR wind measurement campaigns, and computational fluid dynamics modeling. This rigorous site selection process ensures an average capacity factor of 42% — significantly above the UK onshore average of 27%.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            All turbines feature active pitch control, variable speed generators, and SCADA monitoring systems providing real-time performance telemetry to our central operations center in London.
          </p>
        </motion.div>
        <motion.div {...fadeUp} className="space-y-4">
          <div className="relative rounded-2xl overflow-hidden glow-box">
            <img src={turbineCloseup} alt="Wind turbine closeup" className="w-full h-64 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
          </div>
          <div className="relative rounded-2xl overflow-hidden glow-box">
            <img src={windImg} alt="Wind farm at sunset" className="w-full h-48 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
          </div>
        </motion.div>
      </div>
    </Section>

    <div className="overflow-hidden"><EnergyFlowSVG className="h-16 opacity-30" /></div>

    {/* Features */}
    <Section>
      <SectionHeading eyebrow="Capabilities" title="Wind Farm Features" description="Every wind farm is engineered for maximum efficiency, reliability, and longevity." />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { icon: MapPin, title: "Strategic Positioning", desc: "Sites selected from 3+ years of wind measurement data in high-wind corridors verified by independent meteorologists." },
          { icon: Gauge, title: "6.0 MW Rated Power", desc: "Each Vestas V150 turbine is rated at 6.0 MW with 150m rotor diameter and 105m hub height for optimal energy capture." },
          { icon: Plug, title: "Grid Connectivity", desc: "Direct interconnection to national grids via 33kV/132kV substations with full SCADA telemetry and remote operations capability." },
          { icon: Shield, title: "25-Year Lifespan", desc: "Turbines designed for 25+ years operational life with IEC Class II certification for medium-wind environments." },
          { icon: Wrench, title: "Predictive Maintenance", desc: "AI-powered condition monitoring detects anomalies before failure, maintaining 97%+ turbine availability rates." },
          { icon: BarChart3, title: "Real-Time Monitoring", desc: "Every turbine streams performance data to our London operations center — wind speed, power output, generator temperature, pitch angle." },
          { icon: Thermometer, title: "All-Weather Operation", desc: "Cold climate packages and de-icing systems ensure year-round operation from -30°C to +45°C ambient conditions." },
          { icon: Wind, title: "Low Cut-In Speed", desc: "Turbines begin generating at just 3 m/s wind speed, capturing energy even in light wind conditions for maximum annual output." },
        ].map((f, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }} className="p-6 rounded-2xl glass-card glass-card-hover transition-all duration-500 group">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
              <f.icon className="w-6 h-6 text-primary" />
            </div>
            <h3 className="font-display text-sm font-semibold mb-2">{f.title}</h3>
            <p className="text-muted-foreground text-xs leading-relaxed">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>

    {/* Wind Farm Locations */}
    <Section className="bg-secondary/20">
      <SectionHeading eyebrow="Active Locations" title="Our Wind Farm Portfolio" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          { name: "Highland Ridge", location: "Scotland, UK", capacity: "68 MW", turbines: 12, status: "Operational" },
          { name: "North Sea Corridor", location: "Norfolk, UK", capacity: "54 MW", turbines: 9, status: "Operational" },
          { name: "Celtic Wind Park", location: "Wales, UK", capacity: "42 MW", turbines: 7, status: "Operational" },
          { name: "Nordic Breeze", location: "Jutland, Denmark", capacity: "60 MW", turbines: 10, status: "Operational" },
          { name: "Atlantic Gateway", location: "Galway, Ireland", capacity: "48 MW", turbines: 8, status: "Operational" },
          { name: "Pennine Heights", location: "Yorkshire, UK", capacity: "36 MW", turbines: 6, status: "Commissioning" },
        ].map((farm, i) => (
          <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }} className="p-5 rounded-xl glass-card glass-card-hover transition-all duration-500">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-display text-sm font-semibold">{farm.name}</h3>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${farm.status === "Operational" ? "bg-primary/10 text-primary" : "bg-gold/10 text-gold"}`}>
                {farm.status}
              </span>
            </div>
            <div className="space-y-1.5 text-xs text-muted-foreground">
              <div className="flex justify-between"><span>Location</span><span className="text-foreground">{farm.location}</span></div>
              <div className="flex justify-between"><span>Capacity</span><span className="text-foreground">{farm.capacity}</span></div>
              <div className="flex justify-between"><span>Turbines</span><span className="text-foreground">{farm.turbines}</span></div>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  </div>
);

export default WindEnergy;
